/**
 * InsiderScanPage — 2026-10-28 (Bill).
 *
 * The insider scan checks a customer's changed address, phone or e-mail against
 * employees' own customer records and opens a FraudAlert task for the changing
 * officer's supervisor on a match. This page shows whether it is switched on, when
 * the employee list was last built, and each employee's status, so SysOps can see
 * who the scan covers and who it can't (no customer record found, or no employee
 * code in Field 21). Names and statuses only: no customer numbers or contact values
 * ever leave the server.
 *
 * Backing endpoints:
 *   GET  /v3/sysops/insider-scan        — status
 *   POST /v3/sysops/insider-scan/sync   — rebuild the list now
 *
 * The switches themselves live in Admin Panel settings
 * (compliance.insiderScan.enabled / .syncEnabled / .employeeCodes, seed 442).
 */

import { useCallback, useEffect, useState } from 'react';
import { Loader2, ShieldCheck, RefreshCw } from '@retail-ops/panel-shell';
import { useApiClient } from '@/contexts/ApiClientContext';
import type { InsiderScanStatus } from '@retail-ops/api-client';
import { formatLocal } from '@/utils/time';

const STATUS_LABEL: Record<string, string> = {
  matched: 'Covered',
  'no-customer-record': 'No customer record found',
  'no-employee-code': 'No employee code in Field 21',
  error: 'Could not be checked',
};

const STATUS_ORDER = ['error', 'no-employee-code', 'no-customer-record', 'matched'];

export function InsiderScanPage(): JSX.Element {
  const api = useApiClient();
  const [data, setData] = useState<InsiderScanStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('attention');

  const reload = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const resp = await api.retail.compliance.insiderScanStatus();
      if (resp.success) setData(resp);
      else setError(resp.error ?? 'Could not load the insider scan status.');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [api]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const onSync = useCallback(async (): Promise<void> => {
    setSyncing(true);
    setToast(null);
    setError(null);
    try {
      const r = await api.retail.compliance.insiderScanSync();
      if (!r.ran) setToast(r.reason ?? 'The employee list sync is switched off.');
      else if (r.success)
        setToast(`Rebuilt: ${r.employees} staff accounts, ${r.matched} covered, ${r.noCustomerRecord} with no customer record, ${r.noEmployeeCode} with no employee code, ${r.errors} not checked.`);
      else setError(r.reason ?? 'The rebuild failed; the previous list was kept.');
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSyncing(false);
    }
  }, [api, reload]);

  const rows = (data?.employees ?? [])
    .filter((e) => (filter === 'all' ? true : filter === 'attention' ? e.status !== 'matched' : e.status === filter))
    .slice()
    .sort((a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status) || a.displayName.localeCompare(b.displayName));

  return (
    <>
      <h1>Insider scan</h1>
      <p className="page-intro">
        When a customer&apos;s address, phone or e-mail changes, the new value is checked against
        employees&apos; own customer records (any employee code 1&ndash;9 in Field 21). A match opens a
        FraudAlert task for the changing officer&apos;s supervisor. The employee list is every enabled
        staff account in Entra, matched to Core Director by name, and is rebuilt nightly.
      </p>

      {data && (
        <div className="counts-strip">
          <div className={`counts-strip__item ${data.scanEnabled ? 'counts-strip__item--ok' : 'counts-strip__item--warn'}`}>
            <div className="counts-strip__label">Scan</div>
            <div className="counts-strip__value">{data.scanEnabled ? 'On' : 'Off'}</div>
          </div>
          <div className={`counts-strip__item ${data.syncEnabled ? 'counts-strip__item--ok' : 'counts-strip__item--warn'}`}>
            <div className="counts-strip__label">Nightly list build</div>
            <div className="counts-strip__value">{data.syncEnabled ? 'On' : 'Off'}</div>
          </div>
          <div className="counts-strip__item">
            <div className="counts-strip__label">Last built</div>
            <div className="counts-strip__value">{data.lastSyncUtc ? formatLocal(data.lastSyncUtc) : 'Never'}</div>
          </div>
          <div className="counts-strip__item counts-strip__item--ok">
            <div className="counts-strip__label">Covered</div>
            <div className="counts-strip__value">{data.counts['matched'] ?? 0}</div>
          </div>
          <div className="counts-strip__item counts-strip__item--warn">
            <div className="counts-strip__label">Not covered</div>
            <div className="counts-strip__value">
              {(data.counts['no-customer-record'] ?? 0) + (data.counts['no-employee-code'] ?? 0) + (data.counts['error'] ?? 0)}
            </div>
          </div>
        </div>
      )}

      {data && !data.scanEnabled && (
        <div className="banner banner--warn" role="status">
          The scan is switched off, so contact changes are not being checked. To turn it on, set
          {' '}<code>compliance.insiderScan.syncEnabled</code> in Admin Panel settings, rebuild the list
          here, then set <code>compliance.insiderScan.enabled</code>.
        </div>
      )}
      {toast && (
        <div className="banner banner--ok" role="status">
          <ShieldCheck size={16} className="banner__icon" />
          {toast}
        </div>
      )}
      {error && (
        <div className="banner banner--err" role="status">
          {error}
        </div>
      )}

      <div className="toolbar">
        <label className="toolbar__field">
          Show
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="attention">Not covered</option>
            <option value="all">Everyone</option>
            <option value="matched">Covered</option>
            <option value="no-customer-record">No customer record found</option>
            <option value="no-employee-code">No employee code</option>
            <option value="error">Could not be checked</option>
          </select>
        </label>
        <div className="toolbar__spacer" />
        <button type="button" className="btn btn--sm" onClick={() => void onSync()} disabled={syncing}>
          {syncing ? <Loader2 className="spin" size={14} /> : <RefreshCw size={14} />} Rebuild now
        </button>
      </div>

      {!data && !error && (
        <div className="loading-inline">
          <Loader2 className="spin" size={16} /> Loading…
        </div>
      )}

      {data && rows.length === 0 && (
        <div className="empty">
          <ShieldCheck size={32} className="empty__icon" />
          <p>{data.employees.length === 0 ? 'The employee list has not been built yet.' : 'Nobody in this view.'}</p>
        </div>
      )}

      {rows.length > 0 && (
        <table className="data-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Status</th>
              <th>Customer records</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => (
              <tr key={e.displayName + e.checkedAtUtc}>
                <td><strong>{e.displayName}</strong></td>
                <td>{STATUS_LABEL[e.status] ?? e.status}</td>
                <td>{e.matchedCifCount}</td>
                <td className="cell-dim">{e.detail ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
